import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfiguration } from '../../context/ConfigurationContext';
import {
  PRE_SCREENING_QUESTIONS,
  type PreScreeningAnswers,
} from '../../constants/pre-screening-questions';

interface PreScreeningProps {
  onNext: () => void;
  onBack: () => void;
}

const PreScreening: FC<PreScreeningProps> = ({ onNext, onBack }) => {
  const { i18n } = useTranslation();
  const { preScreeningAnswers, setPreScreeningAnswers, completePreScreening } = useConfiguration();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const isFr = i18n.language?.startsWith('fr');

  const totalQuestions = PRE_SCREENING_QUESTIONS.length;
  const progress = ((currentQuestion + 1) / totalQuestions) * 100;

  const handleAnswer = (value: string) => {
    const questionId = PRE_SCREENING_QUESTIONS[currentQuestion].id;
    setPreScreeningAnswers({ [questionId]: value } as Partial<PreScreeningAnswers>);

    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      completePreScreening();
      onNext();
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else {
      onBack();
    }
  };

  const getCurrentAnswer = () => {
    const questionId = PRE_SCREENING_QUESTIONS[currentQuestion].id as keyof typeof preScreeningAnswers;
    return preScreeningAnswers[questionId];
  };

  const renderQuestion = () => {
    const question = PRE_SCREENING_QUESTIONS[currentQuestion];
    const currentAnswer = getCurrentAnswer();

    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-primary/10 text-brand-primary font-bold text-lg mb-4">
            {question.code}
          </div>
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-brand-text dark:text-white">
            {isFr && question.textFr ? question.textFr : question.text}
          </h2>
        </div>

        <div className="grid gap-3">
          {question.options.map((option) => {
            const isSelected = currentAnswer === option.value;
            return (
              <button
                key={option.value}
                onClick={() => handleAnswer(option.value)}
                className={`group relative w-full text-left p-4 sm:p-5 rounded-xl border-2 transition-all duration-200 ${
                  isSelected
                    ? 'border-brand-primary bg-brand-primary/5 shadow-md'
                    : 'border-brand-border dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-brand-primary/50 hover:bg-brand-primary/5'
                }`}
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      isSelected
                        ? 'border-brand-primary bg-brand-primary'
                        : 'border-gray-300 dark:border-slate-600'
                    }`}
                  >
                    {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                  </div>

                  <div className="flex-1">
                    <span className={`text-lg font-medium transition-colors ${
                      isSelected ? 'text-brand-primary' : 'text-brand-text dark:text-white'
                    }`}>
                      {isFr && option.labelFr ? option.labelFr : option.label}
                    </span>
                    {option.description && (
                      <p className="text-sm text-brand-text-light dark:text-slate-400 mt-1">
                        {isFr && option.descriptionFr ? option.descriptionFr : option.description}
                      </p>
                    )}
                  </div>

                  <span className={`material-symbols-outlined transition-opacity ${
                    isSelected ? 'opacity-100 text-brand-primary' : 'opacity-0 group-hover:opacity-50'
                  }`}>
                    arrow_forward
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-brand-bg dark:bg-slate-900 font-body pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-brand-border dark:border-slate-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-brand-text-light hover:text-brand-text dark:text-slate-400 dark:hover:text-white transition-colors"
            >
              <span className="material-symbols-outlined">arrow_back</span>
              <span className="font-medium">{isFr ? 'Retour' : 'Back'}</span>
            </button>

            <span className="font-bold text-brand-text dark:text-white">
              {isFr ? 'Questions préliminaires' : 'Pre-Screening'}
            </span>

            <div className="text-sm text-brand-text-muted">
              {currentQuestion + 1} / {totalQuestions}
            </div>
          </div>

          <div className="mt-4">
            <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-primary transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
        {currentQuestion === 0 && (
          <div className="text-center mb-10 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
            <span className="material-symbols-outlined text-4xl text-blue-500 mb-3">info</span>
            <h3 className="text-lg font-semibold text-brand-text dark:text-white mb-2">
              {isFr ? 'Apprenons à connaître votre proche' : "Let's get to know your loved one"}
            </h3>
            <p className="text-brand-text-light dark:text-slate-400">
              {isFr
                ? 'Ces questions rapides nous aident à recommander les meilleures fonctions de sécurité.'
                : 'These quick questions help us recommend the best features and safety settings.'}
            </p>
          </div>
        )}

        {renderQuestion()}
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-brand-border dark:border-slate-800 p-4">
        <div className="max-w-3xl mx-auto flex flex-col-reverse sm:flex-row items-center justify-between gap-3">
          <button
            onClick={handleBack}
            className="w-full sm:w-auto px-6 py-3 text-brand-text-light dark:text-slate-400 font-medium hover:text-brand-text dark:hover:text-white transition-colors"
          >
            {currentQuestion > 0 ? (isFr ? 'Précédent' : 'Previous') : (isFr ? 'Retour' : 'Back')}
          </button>

          <div className="flex items-center gap-2">
            {PRE_SCREENING_QUESTIONS.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index <= currentQuestion ? 'bg-brand-primary' : 'bg-gray-300 dark:bg-slate-600'
                }`}
              />
            ))}
          </div>

          <div className="hidden sm:block text-sm text-brand-text-muted">
            {currentQuestion < totalQuestions - 1
              ? (isFr ? 'Sélectionnez une option' : 'Select an option to continue')
              : (isFr ? 'Dernière question!' : 'Last question!')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreScreening;
